import { SpinnerLoader } from '@assets/icons'
import { Button, ButtonAction, CustomSelect, ErrorForm, HeaderPage, InputFile, InputForm, SwitchDefault } from '@components/index'
import { useYupValidation } from '@hooks/index'
import { REAL_ESTATE_FILE_TYPE } from '@interfaces/realEstateFiles/enums/realEstateFileType'
import { ICompleteFile } from '@interfaces/realEstateFiles/iCompleteFile'
import { REAL_ESTATE_TYPE } from '@interfaces/realEstates/enums/realEstateType'
import { ICreateUpdateRealEstate } from '@interfaces/realEstates/iCreateRealEstate'
import { IRealEstate } from '@interfaces/realEstates/iRealEstate'
import RealEstateModal, { data } from '@pages/realEstates/realEstateModal'
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api'
import { errorHandler, GetPostCode, RealEstateFileService } from '@services/index'
import RealEstateService from '@services/requests/realEstateService'
import { maskCoin, states } from '@utils/index'
import maskCep from '@utils/mask/cepMask'
import latlngMask from '@utils/mask/latlngMask'
import { maskCoinRawData } from '@utils/mask/maskCoin'
import { realEstatesSchema } from '@yupSchemas/index'
import React from 'react'
import { Controller, useFieldArray, UseFieldArrayAppend, useForm } from 'react-hook-form'
import { BsBookmarkFill } from 'react-icons/bs'
import { MdEdit } from 'react-icons/md'
import { RiDeleteBin5Fill } from 'react-icons/ri'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

export default function UpdateRealEstate(): React.ReactElement {

    const { isLoaded: isMapsLoaded } = useJsApiLoader({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string,
    })

    const navigate = useNavigate()
    const params = useParams()

    const queryPage = useQuery().get('page')
    function useQuery(): URLSearchParams {
        return new URLSearchParams(useLocation().search)
    }

    const categories = [
        { title: 'Em Breve', value: REAL_ESTATE_TYPE.COMMING_SOON },
        { title: 'Empreendimento', value: REAL_ESTATE_TYPE.VENTURE },
        { title: 'Lançamento', value: REAL_ESTATE_TYPE.RELEASE },
        { title: 'Selecionado', value: REAL_ESTATE_TYPE.SELECTED },
    ]

    const [isLoading, setIsLoading] = React.useState(false)
    const [data, setData] = React.useState<IRealEstate | undefined>()
    const [isLoadingGallery, setIsLoadingGallery] = React.useState(false)
    const [isLoadingBanner, setIsLoadingBanner] = React.useState(false)
    const [isLoadingDelete, setIsLoadingDelete] = React.useState(false)
    const [isLoadingSetCover, setIsLoadingSetCover] = React.useState(false)
    const [dataModal, setdataModal] = React.useState<data | undefined>(undefined)
    const [ventureLogoDefault, setVentureLogoDefault] = React.useState<ICompleteFile | null>(null)
    const [catalogDefault, setCatalogDefault] = React.useState<ICompleteFile | null>(null)

    const {
        handleSubmit,
        control,
        formState: { errors, dirtyFields },
        reset,
        watch,
        setValue,
    } = useForm<ICreateUpdateRealEstate>({
        defaultValues: {
            type: data?.type ?? REAL_ESTATE_TYPE.COMMING_SOON,
        },
        resolver: useYupValidation(realEstatesSchema)
    })

    const postcode = watch('postcode')
    const watchFieldType = watch('type')
    const watchCoverFileId = watch('coverFileId')
    const watchLatitude = parseFloat(watch('latitude'))
    const watchLongitude = parseFloat(watch('longitude'))

    const latitude = watchLatitude ? watchLatitude : -27.125866002275146
    const longitude = watchLongitude ? watchLongitude : -48.60332015901804

    const {
        fields: additionalInfo,
        remove: removeAdditionalInfo,
        append: appendAdditionalInfo,
        update: updateAdditionalInfo
    } = useFieldArray<ICreateUpdateRealEstate>({ control, name: 'additionalInfo' })

    const {
        fields: banner,
        remove: removeBanner,
        append: appendBanner
    } = useFieldArray<ICreateUpdateRealEstate>({ control, name: 'banner' })

    const {
        fields: gallery,
        remove: removeGallery,
        append: appendGallery
    } = useFieldArray<ICreateUpdateRealEstate>({ control, name: 'gallery' })

    React.useEffect(() => {
        const getRealEstate = async (): Promise<void> => {
            setIsLoading(true)
            try {
                if (!params.id) throw new Error('Id é necessário!')
                const response = await RealEstateService.getOne(parseInt(params.id))

                // Tratamentos para dados importadosZ
                response.latitude = response.latitude || ''
                response.longitude = response.longitude || ''
                response.url360 = response.url360 || ''
                response.urlVideo = response.urlVideo || ''
                response.price = maskCoinRawData(response.price)
                setData(response)
                reset(response)

                listRealEstateFiles(params.id, REAL_ESTATE_FILE_TYPE.GALLERY, appendGallery)
                listRealEstateFiles(params.id, REAL_ESTATE_FILE_TYPE.BANNER, appendBanner)

                await getLogoCatalog(REAL_ESTATE_FILE_TYPE.VENTURE_LOGO)
                await getLogoCatalog(REAL_ESTATE_FILE_TYPE.CATALOG)

            } catch (err) {
                toast(errorHandler(err).message, { type: 'error' })
                navigate('/imoveis')
            } finally {
                setIsLoading(false)
            }
        }
        getRealEstate()
    }, [queryPage])

    React.useEffect(() => {
        getDataCep(postcode)
    }, [postcode])

    function listDescriptionAddInfo(description: string): string[] {
        return description.split('\n')
    }

    async function submit(values: ICreateUpdateRealEstate): Promise<void> {
        const formData = new FormData()
        formData.append('type', values.type)
        formData.append('active', JSON.stringify(values.active))
        formData.append('name', values.name)
        formData.append('listPosition', JSON.stringify(values.listPosition))
        if (watchFieldType === REAL_ESTATE_TYPE.SELECTED) {
            formData.append('price', JSON.stringify(parseFloat(values.price)))
            formData.append('priceObservation', values.priceObservation)
            formData.append('cnaBanner', JSON.stringify(values.cnaBanner))
        }
        if (watchFieldType === REAL_ESTATE_TYPE.RELEASE || watchFieldType === REAL_ESTATE_TYPE.VENTURE) {
            if (values.ventureLogo && values.ventureLogo !== ventureLogoDefault) {
                formData.append('ventureLogo', values.ventureLogo as File)
            }
            if (values.catalog && values.catalog !== catalogDefault)
                formData.append('catalog', values.catalog as File)
            formData.append('urlVideo', values.urlVideo)
            formData.append('url360', values.url360)
            if (values.additionalInfo && values.additionalInfo.length) {
                formData.append('additionalInfo', JSON.stringify(values.additionalInfo))
            } else {
                formData.append('additionalInfo', JSON.stringify([{}]))
            }
            if (values.ventureLogoToDeleteId)
                formData.append('ventureLogoToDeleteId', JSON.stringify(values.ventureLogoToDeleteId))
            if (values.catalogToDeleteId)
                formData.append('catalogToDeleteId', JSON.stringify(values.catalogToDeleteId))
        }
        if (watchFieldType !== REAL_ESTATE_TYPE.COMMING_SOON) {
            formData.append('description', values.description)
            formData.append('postcode', values.postcode)
            formData.append('state', values.state)
            formData.append('address', values.address)
            formData.append('city', values.city)
            formData.append('number', values.number)
            formData.append('latitude', values.latitude)
            formData.append('longitude', values.longitude)
        }

        setIsLoading(true)
        try {
            if (values.type !== REAL_ESTATE_TYPE.COMMING_SOON && !values.coverFileId) throw new Error('Imagem de capa é obrigatória')

            if (data) {
                await RealEstateService.update(data?.id, formData)
                navigate(`/imoveis?page=${queryPage}`)
                toast('Imóvel atualizado com sucesso!', { type: 'success' })
            }
        } catch (err) {
            toast(errorHandler(err).message, { type: 'error' })
        } finally {
            setIsLoading(false)
        }
    }

    async function listRealEstateFiles(realEstateId: string, type: REAL_ESTATE_FILE_TYPE, append: UseFieldArrayAppend<ICreateUpdateRealEstate, 'gallery' | 'banner' | 'additionalInfo'>): Promise<void> {
        try {
            const response = await RealEstateFileService.list(parseInt(realEstateId), type)
            for (const file of response) {
                if (file.principal) setValue('coverFileId', file.id)
                append({ file })
            }
        } catch (err) {
            toast(errorHandler(err).message, { type: 'error' })
        }
    }

    async function getLogoCatalog(type: REAL_ESTATE_FILE_TYPE): Promise<void> {
        try {
            if (params.id) {
                const response = await RealEstateFileService.list(parseInt(params.id), type)
                const file = response[0]
                if (file) {
                    if (file.type === REAL_ESTATE_FILE_TYPE.VENTURE_LOGO) setVentureLogoDefault(file)
                    else if (file.type === REAL_ESTATE_FILE_TYPE.CATALOG) setCatalogDefault(file)
                }
            }
        } catch (err) {
            toast(errorHandler(err).message, { type: 'error' })
        }
    }

    async function createRealEstateFile(type: REAL_ESTATE_FILE_TYPE, fileBin: File, fileUrl: string, realEstateId?: string): Promise<void> {
        const formData = new FormData()
        formData.append('type', type)
        formData.append('file', fileBin)
        type === REAL_ESTATE_FILE_TYPE.GALLERY ?
            setIsLoadingGallery(true)
            : setIsLoadingBanner(true)
        try {
            if (!realEstateId) throw new Error('Id é necessário!')
            const response = await RealEstateFileService.create(formData, parseInt(realEstateId))
            const file: ICompleteFile = {
                id: response.id,
                fileFromS3: fileUrl,
                name: response.name,
                type: response.type,
                principal: response.principal,
                createdAt: response.createdAt,
                updateAt: response.updateAt
            }
            if (type === REAL_ESTATE_FILE_TYPE.BANNER)
                appendBanner({ file })
            if (type === REAL_ESTATE_FILE_TYPE.GALLERY)
                appendGallery({ file })

            toast('Imagem adicionada com sucesso', { type: 'success' })
        } catch (err) {
            toast(errorHandler(err).message, { type: 'error' })
        } finally {
            setIsLoadingGallery(false)
            setIsLoadingBanner(false)
        }
    }

    async function removeRealEstateFile(fileId: number, remove: () => void, realEstateId?: string): Promise<void> {
        setIsLoadingDelete(true)
        try {
            if (!realEstateId || !fileId) throw new Error('Id é necessário!')
            const response = await RealEstateFileService.remove(fileId, parseInt(realEstateId))
            toast(response.message, { type: 'success' })
            remove()
        } catch (err) {
            toast(errorHandler(err).message, { type: 'error' })
        } finally {
            setIsLoadingDelete(false)
        }
    }

    async function setCover(fileId: number, realEstateId?: string): Promise<void> {
        setIsLoadingSetCover(true)
        try {
            if (!realEstateId || !fileId) throw new Error('Id é necessário!')
            const response = await RealEstateFileService.setCover(fileId, parseInt(realEstateId))
            setValue('coverFileId', fileId)
            toast(response.message, { type: 'success' })
        } catch (err) {
            toast(errorHandler(err).message, { type: 'error' })
        } finally {
            setIsLoadingSetCover(false)
        }
    }

    function readerFile(file: File): string {
        return URL.createObjectURL(file)
    }

    async function getDataCep(value: string): Promise<void> {
        try {
            if (!value || !dirtyFields.postcode || value.replace(/\D/g, '').length !== 8) {
                return
            }
            const dataCep = await GetPostCode(value)
            if (dataCep && dataCep.data) {
                setValue('state', dataCep.data.state)
                setValue('address', dataCep.data.streetAddress)
                setValue('city', dataCep.data.city)
                setValue('latitude', dataCep.data.latitude.toString())
                setValue('longitude', dataCep.data.longitude.toString())
            } else {
                toast('CEP não encontrado!', { type: 'warning' })
            }
        } catch (error) {
            toast('CEP não encontrado!', { type: 'warning' })
        }
    }

    return (
        <div className="card">
            <div className="create-update-real-estate">
                <HeaderPage title="Editar Imóvel" />
                {isLoading ? (
                    <div className="loader-page">
                        <SpinnerLoader />
                    </div>
                ) : data ? (
                    <form
                        onSubmit={handleSubmit(submit)}
                        className='form'
                    >
                        <section className='section-1'>
                            <div className='default-fields'>
                                <Controller
                                    name='type'
                                    control={control}
                                    render={({ field: { value } }) => {
                                        return (
                                            <CustomSelect
                                                label='Tipo'
                                                name='type'
                                                value={value}
                                                onChange={(e) => reset({
                                                    type: e.target.value as REAL_ESTATE_TYPE
                                                })}
                                                options={categories}
                                                style={{ marginBottom: '1.4rem' }}
                                            />
                                        )
                                    }}
                                />
                                <div className="box-controller name">
                                    <Controller
                                        name="name"
                                        control={control}
                                        defaultValue={data?.name ?? ''}
                                        render={({ field: { value, onChange } }) => {
                                            return (
                                                <InputForm
                                                    label="Nome"
                                                    placeholder='Nome do imóvel'
                                                    name="name"
                                                    value={value}
                                                    onChange={onChange}
                                                    size='small'
                                                />
                                            )
                                        }}
                                    />
                                    <ErrorForm error={errors.name?.message} />
                                </div>
                                <div className="box-controller">
                                    <Controller
                                        name="listPosition"
                                        control={control}
                                        defaultValue={data?.listPosition ?? 0}
                                        render={({ field: { value, onChange } }) => {
                                            return (
                                                <InputForm
                                                    type="number"
                                                    placeholder="Posição"
                                                    label="Posição"
                                                    name="listPosition"
                                                    value={value}
                                                    onChange={onChange}
                                                    style={{ width: '9rem' }}
                                                    size='small'
                                                />
                                            )
                                        }}
                                    />
                                    <ErrorForm error={errors.listPosition?.message} />
                                </div>
                                <div className='box-switchs'>
                                    <Controller
                                        name='active'
                                        control={control}
                                        defaultValue={data?.active ?? true}
                                        render={({ field: { value, onChange } }) => {
                                            return (
                                                <div className='status'>
                                                    <label htmlFor='switchStatus' className='switchLabel'>Status</label>
                                                    <div>
                                                        <SwitchDefault
                                                            value={value}
                                                            id='switchStatus'
                                                            checked={value}
                                                            onChange={onChange}
                                                            title={'Ativar/Desativar'}
                                                            color='secondary'
                                                        />
                                                        <p className='small-2'>{value ? 'Ativo' : 'Inativo'}</p>
                                                    </div>
                                                </div>
                                            )
                                        }}
                                    />
                                    {watchFieldType === REAL_ESTATE_TYPE.SELECTED ?
                                        <Controller
                                            name='cnaBanner'
                                            control={control}
                                            defaultValue={data?.cnaBanner ?? true}
                                            render={({ field: { value, onChange } }) => {
                                                return (
                                                    <div className='status'>
                                                        <label htmlFor='switchBannerCna' className='switchLabel'>Exibir como Banner CNA</label>
                                                        <div>
                                                            <SwitchDefault
                                                                value={value}
                                                                onChange={onChange}
                                                                id='switchBannerCna'
                                                                checked={value}
                                                                title={'Sim/Não'}
                                                                color='secondary'
                                                            />
                                                            <p className='small-2'>{value ? 'Sim' : 'Não'}</p>
                                                        </div>
                                                    </div>
                                                )
                                            }}
                                        />
                                        :
                                        <></>
                                    }
                                </div>
                            </div>
                            {watchFieldType === REAL_ESTATE_TYPE.VENTURE || watchFieldType === REAL_ESTATE_TYPE.RELEASE ?
                                <div className='logo-catalog-box'>
                                    <div className='logo-box'>
                                        <h6 className='input-title'>Logo do Empreendimento</h6>
                                        <InputFile
                                            name='ventureLogo'
                                            fileDefault={ventureLogoDefault}
                                            fileFormToDelete={(value) => setValue('ventureLogoToDeleteId', value)}
                                            onChange={(value) => setValue('ventureLogo', value)}
                                            acceptFiles='image/jpeg, image/png'
                                        />
                                    </div>
                                    <div className='pdf-box'>
                                        <h6 className='input-title'>Catálogo PDF</h6>
                                        <InputFile
                                            name='catalog'
                                            fileDefault={catalogDefault}
                                            fileFormToDelete={(value) => setValue('catalogToDeleteId', value)}
                                            onChange={(value) => setValue('catalog', value)}
                                            acceptFiles='application/pdf'
                                        />
                                    </div>
                                </div>
                                :
                                <></>
                            }
                            <div className={`too-many-fields 
                                ${watchFieldType === REAL_ESTATE_TYPE.VENTURE || watchFieldType === REAL_ESTATE_TYPE.RELEASE ? 'only-venture-release' : ''}`
                            }>
                                {watchFieldType !== REAL_ESTATE_TYPE.COMMING_SOON ?
                                    <div className="box-controller">
                                        <Controller
                                            name="description"
                                            control={control}
                                            defaultValue={data?.description ?? ''}
                                            render={({ field: { value, onChange } }) => {
                                                return (
                                                    <InputForm
                                                        label={`${watchFieldType === REAL_ESTATE_TYPE.VENTURE ? 'Descrição do Empreendimento'
                                                            : watchFieldType === REAL_ESTATE_TYPE.RELEASE ? 'Descrição do Lançamento'
                                                                : 'Descrição'}`}
                                                        placeholder={`${watchFieldType === REAL_ESTATE_TYPE.VENTURE ? 'Descrição do Empreendimento'
                                                            : watchFieldType === REAL_ESTATE_TYPE.RELEASE ? 'Descrição do Lançamento'
                                                                : 'Descrição'}`}
                                                        name="description"
                                                        value={value}
                                                        onChange={onChange}
                                                        multiline
                                                        rows={6}
                                                        sx={{
                                                            '& .MuiInputBase-root': {
                                                                height: '10.362rem',
                                                                alignItems: 'flex-start',
                                                                boxSizing: 'border-box'
                                                            }
                                                        }}
                                                    />
                                                )
                                            }}
                                        />
                                        <ErrorForm error={errors.description?.message} />
                                    </div>
                                    : <></>
                                }
                                {watchFieldType === REAL_ESTATE_TYPE.VENTURE || watchFieldType === REAL_ESTATE_TYPE.RELEASE ?
                                    <div className='additional-info'>
                                        <div className='header-additional-info'>
                                            <h5>Descrição dos apartamentos</h5>
                                            <Button
                                                type='button'
                                                onClick={() => {
                                                    setdataModal({
                                                        index: undefined,
                                                        description: '',
                                                        title: ''
                                                    })
                                                }}
                                                placeholder='Adicionar'
                                            />
                                        </div>
                                        <div className='box-additional-info'>
                                            {additionalInfo.map((item, index) => {
                                                if (item && item.title && item.description) {
                                                    return (
                                                        <div key={item.id} className='content-additional-info'>
                                                            <div className='header-box-additional-info'>
                                                                <h5>{item.title}</h5>
                                                                <button title='Editar' type='button' className='button-action-add-info' onClick={() => {
                                                                    setdataModal({
                                                                        index,
                                                                        title: additionalInfo[index].title,
                                                                        description: additionalInfo[index].description
                                                                    })
                                                                }}>
                                                                    <MdEdit className='icon-button-action' />
                                                                </button>
                                                                <button title='Deletar' type='button' className='button-action-add-info' onClick={() => removeAdditionalInfo(index)}>
                                                                    <RiDeleteBin5Fill className='icon-button-action' />
                                                                </button>
                                                            </div>
                                                            <ul className='list-add-info'>
                                                                {listDescriptionAddInfo(item.description).map((item, index) => (
                                                                    <li key={index}>{item}</li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    )
                                                }
                                            })}
                                        </div>
                                        <div className='box-urls'>
                                            <Controller
                                                name="urlVideo"
                                                control={control}
                                                defaultValue={data?.urlVideo ?? ''}
                                                render={({ field: { value, onChange } }) => {
                                                    return (
                                                        <InputForm
                                                            label="Url do Vídeo"
                                                            name="urlVideo"
                                                            value={value}
                                                            onChange={onChange}
                                                            size='small'
                                                        />
                                                    )
                                                }}
                                            />
                                            <Controller
                                                name="url360"
                                                control={control}
                                                defaultValue={data?.url360 ?? ''}
                                                render={({ field: { value, onChange } }) => {
                                                    return (
                                                        <InputForm
                                                            label="Url 360°"
                                                            name="url360"
                                                            value={value}
                                                            onChange={onChange}
                                                            size='small'
                                                        />
                                                    )
                                                }}
                                            />
                                        </div>
                                    </div>
                                    :
                                    <></>
                                }
                                {watchFieldType === REAL_ESTATE_TYPE.SELECTED ?
                                    <div className='bottom-box'>
                                        <div className='left-box'>
                                            <div className="box-controller">
                                                <Controller
                                                    name="price"
                                                    control={control}
                                                    render={({ field: { value, onChange } }) => {
                                                        return (
                                                            <InputForm
                                                                label="Valor"
                                                                name="price"
                                                                value={value}
                                                                onChange={(e) => {
                                                                    onChange(maskCoin(e.target.value))
                                                                }}
                                                                inputProps={{
                                                                    maxLength: 14
                                                                }}
                                                                InputProps={{
                                                                    startAdornment: 'R$',
                                                                }}
                                                                size='small'
                                                            />
                                                        )
                                                    }}
                                                />
                                                <ErrorForm error={errors.price?.message} />
                                            </div>
                                        </div>
                                        <div className='right-box'>
                                            <Controller
                                                name="priceObservation"
                                                control={control}
                                                defaultValue={data?.priceObservation ?? ''}
                                                render={({ field: { value, onChange } }) => {
                                                    return (
                                                        <InputForm
                                                            label="Observações do valor"
                                                            placeholder="Observações do valor"
                                                            name="priceObservation"
                                                            value={value}
                                                            onChange={onChange}
                                                            size='small'
                                                        />
                                                    )
                                                }}
                                            />
                                        </div>
                                    </div>
                                    :
                                    <></>
                                }
                            </div>
                        </section>
                        {watchFieldType !== REAL_ESTATE_TYPE.COMMING_SOON ?
                            <>
                                <section className='section-2'>
                                    <h5>Localização</h5>
                                    <div>
                                        <div className='left-box'>
                                            <div className="box-controller name">
                                                <Controller
                                                    name="postcode"
                                                    control={control}
                                                    defaultValue={data?.postcode ?? ''}
                                                    render={({ field: { value, onChange } }) => {
                                                        return (
                                                            <InputForm
                                                                label="CEP"
                                                                placeholder='CEP do imóvel'
                                                                name="postcode"
                                                                value={maskCep(value)}
                                                                onChange={onChange}
                                                                size='small'
                                                                inputProps={{
                                                                    maxLength: 9
                                                                }}
                                                            />
                                                        )
                                                    }}
                                                />
                                                <ErrorForm error={errors.postcode?.message} />
                                            </div>
                                            <div className="box-controller" style={{ gridColumn: 'span 2' }}>
                                                <Controller
                                                    name="city"
                                                    control={control}
                                                    defaultValue={data?.city ?? ''}
                                                    render={({ field: { value, onChange } }) => {
                                                        return (
                                                            <InputForm
                                                                label="Cidade"
                                                                placeholder='Cidade do imóvel'
                                                                name="city"
                                                                value={value}
                                                                onChange={onChange}
                                                                size='small'
                                                            />
                                                        )
                                                    }}
                                                />
                                                <ErrorForm error={errors.city?.message} />
                                            </div>

                                            <div className="box-controller" style={{ gridColumn: 'span 2' }}>
                                                <Controller
                                                    name="address"
                                                    control={control}
                                                    defaultValue={data?.address ?? ''}
                                                    render={({ field: { value, onChange } }) => {
                                                        return (
                                                            <InputForm
                                                                label="Endereço"
                                                                placeholder='Endereço do imóvel'
                                                                name="address"
                                                                value={value}
                                                                onChange={onChange}
                                                                size='small'
                                                            />
                                                        )
                                                    }}
                                                />
                                                <ErrorForm error={errors.address?.message} />
                                            </div>
                                            <div className="box-controller">
                                                <Controller
                                                    name="number"
                                                    control={control}
                                                    defaultValue={data?.number ?? ''}
                                                    render={({ field: { value, onChange } }) => {
                                                        return (
                                                            <InputForm
                                                                label="Número"
                                                                placeholder='Número do imóvel'
                                                                name="number"
                                                                value={value}
                                                                onChange={onChange}
                                                                size='small'
                                                            />
                                                        )
                                                    }}
                                                />
                                                <ErrorForm error={errors.number?.message} />
                                            </div>
                                            <div className="box-controller" style={{ gridColumn: 'span 3' }}>
                                                <Controller
                                                    name='state'
                                                    control={control}
                                                    defaultValue={data?.state ?? 'none'}
                                                    render={({ field: { value, onChange } }) => {
                                                        return (
                                                            <CustomSelect
                                                                label='Estado'
                                                                placeholder='Selecione...'
                                                                name='state'
                                                                value={value}
                                                                onChange={onChange}
                                                                options={states}
                                                                isFirstNoneDefault
                                                            />
                                                        )
                                                    }}
                                                />
                                                <ErrorForm error={errors.state?.message} />
                                            </div>
                                            <div className='lat-lng-box'>
                                                <div className="box-controller">
                                                    <Controller
                                                        name="latitude"
                                                        control={control}
                                                        defaultValue={data?.latitude ?? '-27.125866002275146'}
                                                        render={({ field: { value, onChange } }) => {
                                                            return (
                                                                <InputForm
                                                                    label="Latitude"
                                                                    placeholder='Latitude'
                                                                    name="latitude"
                                                                    value={latlngMask(value)}
                                                                    onChange={onChange}
                                                                    size='small'
                                                                />
                                                            )
                                                        }}
                                                    />
                                                    <ErrorForm error={errors.latitude?.message} />
                                                </div>
                                                <div className="box-controller">
                                                    <Controller
                                                        name="longitude"
                                                        control={control}
                                                        defaultValue={data?.longitude ?? '-48.60332015901804'}
                                                        render={({ field: { value, onChange } }) => {
                                                            return (
                                                                <InputForm
                                                                    label="Longitude"
                                                                    placeholder='Longitude'
                                                                    name="longitude"
                                                                    value={latlngMask(value)}
                                                                    onChange={onChange}
                                                                    size='small'
                                                                />
                                                            )
                                                        }}
                                                    />
                                                    <ErrorForm error={errors.longitude?.message} />
                                                </div>
                                            </div>
                                        </div>
                                        <div className='right-box'>
                                            {!isMapsLoaded ?
                                                <></>
                                                :
                                                <GoogleMap
                                                    zoom={15}
                                                    center={{
                                                        lat: latitude,
                                                        lng: longitude
                                                    }}
                                                    mapContainerClassName='map-container'
                                                    onClick={ev => {
                                                        if (ev.latLng) {
                                                            setValue('latitude', ev.latLng.lat().toString())
                                                            setValue('longitude', ev.latLng.lng().toString())
                                                        }
                                                    }}
                                                >
                                                    <Marker
                                                        position={{
                                                            lat: latitude,
                                                            lng: longitude
                                                        }}
                                                    />
                                                </GoogleMap>
                                            }
                                        </div>
                                    </div>
                                </section>
                                {watchFieldType === REAL_ESTATE_TYPE.RELEASE || watchFieldType === REAL_ESTATE_TYPE.VENTURE ?
                                    <section className='section-3'>
                                        <h5>Banners</h5>
                                        <div className='box-images'>
                                            {banner.map((item, index) => {
                                                const file = item.file as ICompleteFile
                                                return (
                                                    <div key={item.id} className='box-image'>
                                                        <img src={file.fileFromS3} alt="banner" />
                                                        <div className='cover-image'>
                                                            <ButtonAction title='Deletar' isLoading={isLoadingDelete} type='button' onClick={() => removeRealEstateFile(file.id, () => removeBanner(index), params.id)}>
                                                                <RiDeleteBin5Fill className='icon-button-action' />
                                                            </ButtonAction>
                                                        </div>
                                                    </div>
                                                )
                                            })}
                                            <InputFile
                                                onChange={fileBin => {
                                                    if (fileBin)
                                                        createRealEstateFile(REAL_ESTATE_FILE_TYPE.BANNER, fileBin, readerFile(fileBin), params.id)
                                                }}
                                                isLoadingFile={isLoadingBanner}
                                                acceptFiles='image/jpeg, image/png'
                                                multiple
                                            />
                                        </div>
                                    </section>
                                    : <></>
                                }
                                <section className='section-4'>
                                    <h5>Galeria</h5>
                                    <div className='box-images'>
                                        {gallery.map((item, index) => {
                                            const file = item.file as ICompleteFile
                                            return (
                                                <div key={item.id} className='box-image'>
                                                    <img src={file.fileFromS3} alt="banner" />

                                                    {watchCoverFileId === file.id ?
                                                        <div className='cover-checked'>
                                                            <BsBookmarkFill />
                                                        </div>
                                                        :
                                                        <></>
                                                    }

                                                    <div className='cover-image'>
                                                        <ButtonAction
                                                            title='Deletar'
                                                            isLoading={isLoadingDelete}
                                                            isDisabled={watchCoverFileId === file.id}
                                                            type='button'
                                                            onClick={
                                                                () => removeRealEstateFile(file.id, () => removeGallery(index), params.id)
                                                            }
                                                        >
                                                            <RiDeleteBin5Fill className='icon-button-action' />
                                                        </ButtonAction>
                                                        <ButtonAction
                                                            title='Definir como capa'
                                                            isLoading={isLoadingSetCover}
                                                            isDisabled={watchCoverFileId === file.id}
                                                            type='button'
                                                            onClick={
                                                                () => setCover(file.id, params.id)
                                                            }
                                                        >
                                                            <BsBookmarkFill className='icon-button-action' />
                                                        </ButtonAction>
                                                    </div>
                                                </div>
                                            )
                                        })}
                                        <InputFile
                                            onChange={fileBin => {
                                                if (fileBin)
                                                    createRealEstateFile(REAL_ESTATE_FILE_TYPE.GALLERY, fileBin, readerFile(fileBin), params.id)
                                            }}
                                            isLoadingFile={isLoadingGallery}
                                            acceptFiles='image/jpeg, image/png'
                                            multiple
                                        />
                                    </div>
                                    <ErrorForm error={errors.gallery?.message} />
                                </section>
                            </>
                            :
                            <></>
                        }
                        <div className={`box-buttons ${watchFieldType === REAL_ESTATE_TYPE.COMMING_SOON ? 'box-buttons-small' : ''}`}>
                            <Button type="button" secondary placeholder={'Cancelar'} onClick={() => navigate(`/imoveis?page=${queryPage}`)} />
                            <Button type="submit" placeholder={'Salvar'} />
                        </div>
                    </form>
                ) : <></>
                }
            </div>
            <RealEstateModal
                data={dataModal}
                modelTitle={'Descrição do Apartamento'}
                labelTitle={'Título do Apartamento'}
                labelDescription={'Descrição do Apartamento'}
                appendChild={(e: data) => appendAdditionalInfo(e)}
                updateChild={(index, e) => updateAdditionalInfo(index, e)}
                closeModal={() => {
                    setdataModal(undefined)
                }}
            />
        </div>
    )
}